import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Search, ArrowUp, ArrowDown, ArrowRight, ChevronRight, BarChart2, Shield, AlertTriangle, Play, Send, Info, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import logoIcon from '../assets/logo2.png';
import assessmentService from '../services/assessmentService';
import { toast } from 'react-hot-toast';

// Hardcoded AI Enablement data
const aiEnablementData = [
  {
    tool: "Coding Assistant",
    examples: "GitHub Copilot/Codeium/Codey",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "AI Code Review",
    examples: "Amazon CodeGuru, DeepCode",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "AI Writing Assistant",
    examples: "ChatGPT Teams, Jasper",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "AI Design / Image Gen",
    examples: "Midjourney, Firefly, DALL·E",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "Automated Documentation",
    examples: "Mintlify AI, Swimm AI Doc, OpenAI GPT doc bot",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "AI Meeting & On‑boarding Assistants",
    examples: "Otter.ai, Fireflies.ai, Loom AI, GuidePilot",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "AI Data Exploration",
    examples: "ThoughtSpot AI, Mode AI Notebooks, Hex Magic AI",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "AI Agent Frameworks",
    examples: "LangChain, CrewAI, AutoGen, OpenAI GPT Actions",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  },
  {
    tool: "ML Ops Automation",
    examples: "DataRobot, Vertex AI Pipelines, Hugging Face AutoTrain",
    adoption: 0,
    status: "missing",
    tip: "Awaiting assessment"
  }
];

const radarData = [
  { subject: 'Coding Assistant', A: 0, B: 0 },
  { subject: 'Documentation', A: 0, B: 0 },
  { subject: 'AI Agent Frameworks', A: 0, B: 0 },
  { subject: 'Data Exploration', A: 0, B: 0 },
  { subject: 'ML Ops', A: 0, B: 0 }
];

function Dashboard() {
  const [timeRange, setTimeRange] = useState('6m');
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRemediationMessage, setShowRemediationMessage] = useState(false);
  const [showAnalyticsMessage, setShowAnalyticsMessage] = useState(false);
  const [showTrackingMessage, setShowTrackingMessage] = useState(false);
  const [showExportMessage, setShowExportMessage] = useState(false);
  const [showNewAssessmentMessage, setShowNewAssessmentMessage] = useState(false);

  // Default trend data with score of 0 for all months
  const defaultTrendData = [
    { month: 'Jan', rating: 0, baseline: 0 },
    { month: 'Feb', rating: 0, baseline: 0 },
    { month: 'Mar', rating: 0, baseline: 0 },
    { month: 'Apr', rating: 0, baseline: 0 },
    { month: 'May', rating: 0, baseline: 0 },
    { month: 'Jun', rating: 0, baseline: 0 }
  ];

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await assessmentService.getDashboardData();
      // If API returns data, use it; otherwise use default data with all zeros
      if (data?.assessment?.overall) {
        const score = data.assessment.overall;
        const trendData = defaultTrendData.map(item => ({
          ...item,
          rating: item.month === 'Apr' ? score : 0
        }));
        setDashboardData({
          ...data,
          trends: trendData
        });
      } else {
        setDashboardData({
          trends: defaultTrendData
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'enabled':
        return <span className="px-2 py-1 text-xs rounded-full bg-primary/20 text-primary">🟢 Enabled</span>;
      case 'partial':
        return <span className="px-2 py-1 text-xs rounded-full bg-warning/20 text-warning">🟡 Partial</span>;
      case 'missing':
        return <span className="px-2 py-1 text-xs rounded-full bg-danger/20 text-danger">🔴 Missing</span>;
      default:
        return null;
    }
  };

  const handleRemediationClick = () => {
    setShowRemediationMessage(true);
    setTimeout(() => {
      setShowRemediationMessage(false);
    }, 3000);
  };

  const handleAnalyticsClick = () => {
    setShowAnalyticsMessage(true);
    setTimeout(() => {
      setShowAnalyticsMessage(false);
    }, 3000);
  };

  const handleTrackingClick = () => {
    setShowTrackingMessage(true);
    setTimeout(() => {
      setShowTrackingMessage(false);
    }, 3000);
  };

  const handleExportClick = () => {
    setShowExportMessage(true);
    setTimeout(() => {
      setShowExportMessage(false);
    }, 3000);
  };

  const handleNewAssessmentClick = () => {
    setShowNewAssessmentMessage(true);
    setTimeout(() => {
      setShowNewAssessmentMessage(false);
    }, 3000);
  };

  if (loading && !dashboardData) {
    return <div className="p-4">Loading dashboard data...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  return (
    <>
      {/* Dashboard Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Company Dashboard</h1>
        <p className="text-text-secondary">Monitor and manage your AI compliance and trust ratings.</p>
      </div>

      {/* Summary Tiles */}
      <div className="grid grid-cols-3 gap-8 mb-8">
        <div className="bg-background-light rounded-xl border border-primary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Current AI Trust Rating</h3>
            <span className={`px-3 py-1 rounded-full text-sm ${
              dashboardData?.assessment?.overall >= 80 ? 'bg-primary/20 text-primary' :
              dashboardData?.assessment?.overall >= 60 ? 'bg-warning/20 text-warning' :
              'bg-danger/20 text-danger'
            }`}>
              {dashboardData?.assessment?.overall || 0}%
            </span>
          </div>
          <p className="text-text-secondary mb-2">Grade: {dashboardData?.assessment?.grade || 'Not Assessed'}</p>
          <p className="text-sm text-text-secondary">
            Last Assessed: {dashboardData?.assessment?.created_at ? new Date(dashboardData.assessment.created_at).toLocaleString() : 'Not available'}
          </p>
        </div>

        <div className="bg-background-light rounded-xl border border-primary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Pending Assessments</h3>
            <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm">
              {dashboardData?.pending_assessments?.length || 0} in Progress
            </span>
          </div>
          <p className="text-text-secondary mb-2">
            {dashboardData?.pending_assessments?.length ? `${dashboardData.pending_assessments.length} assessments scheduled` : 'No assessments scheduled'}
          </p>
          <button className="text-primary hover:text-primary-light transition-colors flex items-center gap-1">
            View Details <ChevronRight size={16} />
          </button>
        </div>

        <div className="bg-background-light rounded-xl border border-primary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Compliance Status</h3>
            <span className={`px-3 py-1 rounded-full text-sm ${
              dashboardData?.compliance?.overall >= 80 ? 'bg-primary/20 text-primary' :
              dashboardData?.compliance?.overall >= 60 ? 'bg-warning/20 text-warning' :
              'bg-danger/20 text-danger'
            }`}>
              {dashboardData?.compliance?.overall || 0}%
            </span>
          </div>
          <p className="text-text-secondary mb-2">Status: {dashboardData?.compliance?.status || 'Not Assessed'}</p>
          <p className="text-sm text-text-secondary">
            Last Updated: {dashboardData?.compliance?.last_updated ? new Date(dashboardData.compliance.last_updated).toLocaleString() : 'Not available'}
          </p>
        </div>
      </div>

      {/* Quick Access Buttons */}
      <div className="flex gap-4 mb-8 items-center relative">
        <div className="flex items-center gap-2 relative">
          <button 
            onClick={handleNewAssessmentClick}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
          >
            <BarChart2 size={20} /> New Assessment
          </button>
          {showNewAssessmentMessage && (
            <div 
              className={`
                absolute left-1/2 -translate-x-1/2 bottom-full mb-2 
                bg-background-light border border-primary/20 rounded-lg 
                px-4 py-2 text-sm text-text-secondary
                shadow-lg z-50 flex items-center
                transition-opacity duration-300 ease-in-out
                ${showNewAssessmentMessage ? 'opacity-100' : 'opacity-0'}
              `}
            >
              We're working on it! Check back shortly.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 relative">
          <button 
            onClick={handleRemediationClick}
            className="flex items-center gap-2 px-4 py-2 bg-background-light text-text rounded-lg hover:bg-primary/10 transition-colors"
          >
            <Shield size={20} /> Remediation Actions
          </button>
          {showRemediationMessage && (
            <div 
              className={`
                absolute left-1/2 -translate-x-1/2 bottom-full mb-2 
                bg-background-light border border-primary/20 rounded-lg 
                px-4 py-2 text-sm text-text-secondary
                shadow-lg z-50 flex items-center
                transition-opacity duration-300 ease-in-out
                ${showRemediationMessage ? 'opacity-100' : 'opacity-0'}
              `}
            >
              We're working on it! Check back shortly.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 relative">
          <button 
            onClick={handleAnalyticsClick}
            className="flex items-center gap-2 px-4 py-2 bg-background-light text-text rounded-lg hover:bg-primary/10 transition-colors"
          >
            <AlertTriangle size={20} /> Analytics
          </button>
          {showAnalyticsMessage && (
            <div 
              className={`
                absolute left-1/2 -translate-x-1/2 bottom-full mb-2 
                bg-background-light border border-primary/20 rounded-lg 
                px-4 py-2 text-sm text-text-secondary
                shadow-lg z-50 flex items-center
                transition-opacity duration-300 ease-in-out
                ${showAnalyticsMessage ? 'opacity-100' : 'opacity-0'}
              `}
            >
              We're working on it! Check back shortly.
            </div>
          )}
        </div>
      </div>

      {/* Recent Trends Chart */}
      <div className="bg-background-light rounded-lg p-6 border border-primary/20">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-text">AI Trust Rating Trends</h3>
          <div className="flex items-center gap-4">
            {!isCustomRange ? (
              <select
                value={timeRange}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setIsCustomRange(true);
                  } else {
                    setTimeRange(e.target.value);
                  }
                }}
                className="bg-background border border-primary/20 rounded-lg px-4 py-2 text-sm"
              >
                <option value="6m">Last 6 Months</option>
                <option value="1y">Last Year</option>
                <option value="2y">Last 2 Years</option>
                <option value="custom">Custom Range</option>
              </select>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-background border border-primary/20 rounded-lg px-4 py-2 text-sm"
                />
                <span className="text-text-secondary">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-background border border-primary/20 rounded-lg px-4 py-2 text-sm"
                />
                <button
                  onClick={() => {
                    setIsCustomRange(false);
                    setTimeRange('6m');
                  }}
                  className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#5ECF9E]"></div>
              <span className="text-sm text-text">Your Company</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-text/20"></div>
              <span className="text-sm text-text">Industry Baseline</span>
            </div>
          </div>
        </div>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={dashboardData?.trends?.length ? dashboardData.trends : defaultTrendData} 
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#5ECF9E" opacity={0.1} />
              <XAxis 
                dataKey="month" 
                stroke="#5ECF9E"
                tick={{ fill: '#5ECF9E' }}
              />
              <YAxis 
                domain={[0, 100]}
                stroke="#5ECF9E"
                tick={{ fill: '#5ECF9E' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid #5ECF9E',
                  borderRadius: '8px'
                }}
                labelStyle={{ color: '#FFD700' }}
                itemStyle={(entry) => ({
                  color: entry.dataKey === 'baseline' ? '#666' : '#5ECF9E'
                })}
                formatter={(value, name) => [`${value}%`, name === 'rating' ? 'Your Company' : 'Industry Baseline']}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Line 
                type="monotone" 
                dataKey="rating" 
                stroke="#5ECF9E" 
                strokeWidth={3}
                dot={{ 
                  fill: '#5ECF9E', 
                  strokeWidth: 2, 
                  r: 6,
                  stroke: '#fff'
                }}
                activeDot={{ 
                  r: 8, 
                  fill: '#5ECF9E',
                  stroke: '#fff',
                  strokeWidth: 2
                }}
              />
              <Line 
                type="monotone" 
                dataKey="baseline" 
                stroke="#5ECF9E" 
                strokeWidth={2}
                strokeDasharray="5 5"
                opacity={0.5}
                dot={{ 
                  fill: '#5ECF9E', 
                  strokeWidth: 2, 
                  r: 4,
                  stroke: '#fff'
                }}
                activeDot={{ 
                  r: 6, 
                  fill: '#5ECF9E',
                  stroke: '#fff',
                  strokeWidth: 2
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Enablement Radar */}
      <div className="bg-background-light rounded-xl border border-primary/20 p-6 mt-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">AI Enablement Radar</h3>
              <p className="text-text-secondary mt-1">Track internal adoption of high‑impact AI tools and spot opportunities.</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <button 
                  onClick={handleTrackingClick}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
                >
                  <Play size={16} />
                  Activate Live Tracking
                </button>
                {showTrackingMessage && (
                  <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-background-light border border-primary/20 rounded-lg shadow-lg z-50 transition-opacity duration-300">
                    We're working on it! Check back shortly.
                  </div>
                )}
              </div>
              <button className="text-text-secondary hover:text-primary transition-colors">
                <Info size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* Radar Chart */}
          <div className="h-80">
            <div className="mb-2 text-sm text-text-secondary">Top 5 Strategic AI Tools vs Industry Baseline</div>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#333" />
                <PolarAngleAxis dataKey="subject" stroke="#fff" />
                <PolarRadiusAxis stroke="#fff" />
                <Radar
                  name="Your Company"
                  dataKey="A"
                  stroke="#5ECF9E"
                  fill="#5ECF9E"
                  fillOpacity={0.3}
                />
                <Radar
                  name="Industry Baseline"
                  dataKey="B"
                  stroke="#666"
                  fill="#666"
                  fillOpacity={0.3}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #5ECF9E',
                    borderRadius: '8px',
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* AI Tools Table */}
          <div className="overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto pr-4 -mr-4">
              <table className="w-full">
                <thead className="sticky top-0 bg-background-light z-10">
                  <tr className="border-b border-primary/20">
                    <th className="text-left pb-4 text-text-secondary font-medium">AI Tool</th>
                    <th className="text-center pb-4 text-text-secondary font-medium">Adoption %</th>
                    <th className="text-right pb-4 text-text-secondary font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary/10">
                  {aiEnablementData.map((item, index) => (
                    <tr key={index} className="group hover:bg-primary/5 transition-colors">
                      <td className="py-4">
                        <div>
                          <div className="font-medium">{item.tool}</div>
                          <div className="text-sm text-text-secondary">{item.examples}</div>
                        </div>
                      </td>
                      <td className="py-4 text-center">{item.adoption}%</td>
                      <td className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {getStatusBadge(item.status)}
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="relative">
                              <Info size={16} className="text-text-secondary cursor-help" />
                              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 text-sm bg-background-light border border-primary/20 rounded-lg shadow-lg">
                                {item.tip}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="mt-8 flex justify-end">
        <div className="relative">
          <button 
            onClick={handleExportClick}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
          >
            <Send size={20} /> Export Compliance Report (PDF)
          </button>
          {showExportMessage && (
            <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-background-light border border-primary/20 rounded-lg shadow-lg z-50 transition-opacity duration-300">
              We're working on it! Check back shortly.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Dashboard; 